import appConfig from '../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.creditAnalysisApiEnv;

const headers = (): Record<string, string> => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (API_KEY) h['x-api-key'] = API_KEY;
    return h;
};

export interface CreateBatchRequest {
    batchName: string;
    assignor: { uuid: string; name: string; document: string };
    items: {
        document: string;
        draweeName: string;
        requestedAmount: number;
        dueDate?: string;
        observation?: string;
    }[];
}

export interface CreateBatchResponse {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    totalItems: number;
    gatheredCount: number;
    errorCount: number;
    analysisDispatched: boolean;
}

export interface BatchListItem {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    assignorName: string;
    totalItems: number;
    gatheredCount: number;
    errorCount: number;
    approvedCount: number;
    rejectedCount: number;
    pendingCount: number;
    createdAt: string;
}

export interface BatchDetail {
    id: string;
    batchNumber: string;
    batchName: string;
    status: string;
    assignorName: string;
    assignorDocument: string;
    createdAt: string;
    items: BatchDetailItem[];
}

export interface BatchDetailItem {
    id: string;
    document: string;
    draweeName: string;
    requestedAmount: number;
    status: string;
    observation?: string;
    companyCity?: string;
    companyState?: string;
    companyRoot?: string;
    companyPersonType?: string;
    companyFoundingYear?: string;
    companyQsa?: number;
    exposureScrTotal?: number;
    exposureScrRural?: number;
    exposureOverdue?: number;
    exposureCurrent?: number;
    exposurePotential?: number;
    financialAnnualRevenue?: number;
    financialEquity?: number;
    financialApprovedCreditLimit?: number;
    financialRating?: string;
    pafDate?: string;
    previousAnalysis: boolean;
    bureauStatuses?: Record<string, string>;
    bureauErrors?: Record<string, string>;
    errorMessage?: string;
}

export async function createBatchProcessing(request: CreateBatchRequest): Promise<CreateBatchResponse> {
    const response = await fetch(`${API_URL}/batch-processing`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(request),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function listBatchProcessing(): Promise<{ total: number; items: BatchListItem[] }> {
    const response = await fetch(`${API_URL}/batch-processing`, { headers: headers() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getBatchDetail(batchId: string): Promise<BatchDetail> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}`, { headers: headers() });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}


export async function refreshPaf(batchId: string, itemIds: string[]): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/refresh-paf`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ itemIds }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function saveDecision(
    batchId: string,
    itemId: string,
    decision: { status: string; approvedValue?: number; justification?: string; creditObs?: string; cprNumber?: string; decidedBy?: string },
): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/analysis-items/${itemId}/decision`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ batchId, ...decision }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function saveBatchDecision(
    batchId: string,
    items: { itemId: string; approvedValue?: string }[],
    decision: { status: string; justification?: string; creditObs?: string; decidedBy?: string },
): Promise<{ updated: number }> {
    const response = await fetch(`${API_URL}/analysis-items/batch-decision`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ batchId, items, ...decision }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getFeatureFlags(): Promise<Record<string, boolean>> {
    const response = await fetch(`${API_URL}/feature-flags`, { headers: headers() });
    if (!response.ok) return {};
    return response.json();
}


export async function reprocessBatch(batchId: string): Promise<{ regathered: number; status: string; dispatched: boolean }> {
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/reprocess`, {
        method: 'POST',
        headers: headers(),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function continueAnalysis(batchId: string, itemIds: string[], bureauCode?: string): Promise<{ subBatchId: string; itemCount: number; pipeline: string[] }> {
    const body: any = { itemIds };
    if (bureauCode) body.bureauCode = bureauCode;
    const response = await fetch(`${API_URL}/batch-processing/${batchId}/continue-analysis`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
}

export async function getColumnTemplates(): Promise<Record<string, Record<number, string>>> {
    const response = await fetch(`${API_URL}/column-templates`, { headers: headers() });
    if (!response.ok) return {};
    return response.json();
}

export async function saveColumnTemplate(name: string, columnTags: Record<number, string>): Promise<void> {
    const res = await fetch(`${API_URL}/column-templates`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ name, columnTags }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function deleteColumnTemplate(name: string): Promise<void> {
    const res = await fetch(`${API_URL}/column-templates/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: headers(),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export interface PafAnalysisData {
    document: string;
    generalData: {
        name: string;
        tradeName: string | null;
        city: string | null;
        state: string | null;
        rating: string | null;
        creditOpinion: string | null;
        economicGroup: string | null;
        approvedCreditLimit: number | null;
        netRevenue: number | null;
    };
    periods: { closingDate: string | null; data: Record<string, string> }[];
    references: { date: string | null; name: string; source: string; comment: string }[];
    exposure: number | null;
}

export async function getPafAnalysis(batchId: string, itemId: string): Promise<PafAnalysisData> {
    const res = await fetch(`${API_URL}/batch-processing/${batchId}/items/${itemId}/paf-analysis`, { headers: headers() });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
}
